import React from "react";
import { Dropdown } from "semantic-ui-react";
import { getMenuWidth, MAX_MENU_WIDTH } from "./utils";
import { Icon } from "../..";
import { ClippedContent } from "../../atoms/ClippedContent";

const MENU_GAP = 1;

const MenuLevel = ({
    items,
    level = 0,
    position,
    onSelect,
    selectedValue,
    hoveredPath,
    setHoveredPath,
    submenuPositions,
    setSubmenuPositions,
    parentWidth = 180,
    flatMode = false, // when true, children of headers render inline
    isError,
}) => {
    const calculatedWidth = getMenuWidth(items);
    const menuWidth = Math.min(
        MAX_MENU_WIDTH,
        Math.max(parentWidth, calculatedWidth)
    );

    const isRoot = level === 0;
    return (
        <Dropdown.Menu
            className="nested-field-dropdown__menu"
            style={{
                ...(isRoot
                    ? {}
                    : {
                          top: position?.top,
                          left: position?.left,
                          maxHeight: position?.maxHeight || 400,
                          overflowY: "auto",
                      }),
                width: isRoot ? "100%" : menuWidth,
                zIndex: 1000 + level,
                position: isRoot ? "relative" : "fixed",
            }}
        >
            {items?.map?.((item) => {
                if (item.primary_header || item.secondary_header) {
                    if (item?.standalone) {
                        const isSelected =
                            selectedValue && selectedValue === item.value;
                        return (
                            <Dropdown.Item
                                key={item.key}
                                className={`nested-field-dropdown__item nested-field-dropdown__item--secondary ${
                                    isSelected ? "selected" : ""
                                }`}
                                onMouseEnter={() => {
                                    if (!flatMode)
                                        setHoveredPath((prev) =>
                                            prev.slice(0, level)
                                        );
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onSelect?.(item);
                                }}
                            >
                                <ClippedContent popupContent={item.text}>
                                    {item.text}
                                </ClippedContent>
                            </Dropdown.Item>
                        );
                    }
                    return (
                        <React.Fragment key={item.key}>
                            <Dropdown.Header
                                className={`nested-field-dropdown__${
                                    item.secondary_header
                                        ? "secondary-header"
                                        : "primary-header"
                                } ${isError ? "error" : ""}`}
                                onMouseEnter={() => {
                                    if (!flatMode)
                                        setHoveredPath((prev) =>
                                            prev.slice(0, level)
                                        );
                                }}
                            >
                                {item.text}
                            </Dropdown.Header>
                            {item.children?.map((child) => {
                                const isSelected = selectedValue && selectedValue === child.value;

                                if (child.type === "sub-header") {
                                    return (
                                        <React.Fragment key={child.key}>
                                            <Dropdown.Header
                                                className={`nested-field-dropdown__sub-header ${
                                                    isError ? "error" : isSelected ? "selected" : ""
                                                }`}
                                                onMouseEnter={() => {
                                                    if (!flatMode)
                                                        setHoveredPath((prev) =>
                                                            prev.slice(0, level)
                                                        );
                                                }}
                                            >
                                                {child.text}
                                            </Dropdown.Header>
                                        </React.Fragment>
                                    );
                                }
                                if (child.type === "flat-leaf") {
                                    return (
                                        <Dropdown.Item
                                            key={child.key}
                                            className={`nested-field-dropdown__item nested-field-dropdown__item--flat ${
                                                isSelected
                                                    ? "selected"
                                                    : ""
                                            }`}
                                            onMouseEnter={() => {
                                                if (!flatMode)
                                                    setHoveredPath((prev) =>
                                                        prev.slice(0, level)
                                                    );
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onSelect?.(child);
                                            }}
                                        >
                                            <ClippedContent popupContent={child.text}>
                                                {child.text}
                                            </ClippedContent>
                                        </Dropdown.Item>
                                    );
                                }
                                if (child.type === "flyout") {
                                    return renderFlyoutItem(child);
                                }
                                return null;
                            })}
                        </React.Fragment>
                    );
                }

                if (item.secondary_header) {
                    return (
                        <React.Fragment key={item.key}>
                            <Dropdown.Header
                                className="nested-field-dropdown__secondary-header"
                                onMouseEnter={() => {
                                    if (!flatMode)
                                        setHoveredPath((prev) =>
                                            prev.slice(0, level)
                                        );
                                }}
                            >
                                {item.text}
                            </Dropdown.Header>
                        </React.Fragment>
                    );
                }

                // Flyout item
                if (item.type === "flyout" || item.children?.length) {
                    return renderFlyoutItem(item);
                }

                // Regular leaf
                return (
                    <Dropdown.Item
                        key={item.key}
                        className={`nested-field-dropdown__item ${
                            selectedValue && selectedValue === item.value ? "selected" : ""
                        }`}
                        onMouseEnter={() => {
                            if (!flatMode)
                                setHoveredPath((prev) => prev.slice(0, level));
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelect?.(item);
                        }}
                    >
                        <ClippedContent popupContent={item.text}>{item.text}</ClippedContent>
                    </Dropdown.Item>
                );
            })}
        </Dropdown.Menu>
    );

    function renderFlyoutItem(item) {
        const active = hoveredPath?.[level] === item.key;
        const hasSelectedChild = (item, selectedValue) => {
            if (!item?.children?.length) {
                return false;
            }

            return item.children.some((child) => {
                return (
                    child.value === selectedValue ||
                    hasSelectedChild(child, selectedValue)
                );
            });
        };

        const isSelected =
            !!selectedValue &&
            (selectedValue === item?.value || hasSelectedChild(item, selectedValue));
        return (
            <Dropdown.Item
                key={item.key}
                className={`nested-field-dropdown__item ${
                    isSelected ? "selected" : ""
                }`}
                onMouseEnter={(e) => {
                    if (!item.children?.length) {
                        return setHoveredPath((prev) => prev.slice(0, level));
                    }
                    const rect = e.currentTarget.getBoundingClientRect();
                    const childWidth = getMenuWidth(item.children || []);
                    let left = rect.right + MENU_GAP;
                    if (left + childWidth > window.innerWidth) {
                        left = rect.left - childWidth - MENU_GAP;
                    }
                    let top = rect.top;
                    const maxAllowedHeight = Math.min(
                        400,
                        window.innerHeight - 16
                    );
                    const estimatedHeight = Math.min(
                        maxAllowedHeight,
                        (item.children?.length || 1) * 40
                    );
                    if (top + estimatedHeight > window.innerHeight - 8) {
                        top = window.innerHeight - estimatedHeight - 8;
                    }

                    top = Math.max(8, top);
                    setHoveredPath((prev) => {
                        const next = [...prev];
                        next[level] = item.key;
                        return next.slice(0, level + 1);
                    });
                    setSubmenuPositions((prev) => ({
                        ...prev,
                        [item.key]: { top, left, maxHeight: maxAllowedHeight },
                    }));
                }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <div className={`nested-field-dropdown__item-left ${isSelected ? "selected" : ""}`}>
                    <ClippedContent popupContent={item.text}>{item.text}</ClippedContent>
                </div>
                <Icon name="angle right" />
                {active && submenuPositions?.[item.key] && (
                    <MenuLevel
                        items={item.children}
                        level={level + 1}
                        position={submenuPositions[item.key]}
                        onSelect={onSelect}
                        selectedValue={selectedValue}
                        hoveredPath={hoveredPath}
                        setHoveredPath={setHoveredPath}
                        submenuPositions={submenuPositions}
                        setSubmenuPositions={setSubmenuPositions}
                        parentWidth={menuWidth}
                    />
                )}
            </Dropdown.Item>
        );
    }
};

export default MenuLevel;
